/* eslint-disable max-lines */
import { CountryCode as LibPhoneNumberCountryCode } from 'libphonenumber-js/max';

import { CountryRegion } from '@Common/countries/regions';
import { LegalEntity } from '@Common/types/bo/users/users';
import { InstrumentsUniverses } from '@Common/types/instruments/universes';
import { CountryStatus } from '@Common/types/list/countries';

export type UnsupportedCountries =
  | 'AC'
  | 'AI'
  | 'AS'
  | 'AW'
  | 'BQ'
  | 'CW'
  | 'EH'
  | 'IO'
  | 'PM'
  | 'SX'
  | 'TA';

export type SupportedCountryCodes = Exclude<LibPhoneNumberCountryCode, UnsupportedCountries>;

export type CountryData = {
  code: SupportedCountryCodes;
  code3: string;
  dialCode: string;
  emoji: string;
  name: string;
  adjective: string;
  currency: string;
  isPassportPriorityIdentifier: boolean;
  isCONCAT: boolean;
  symbol?: string;
  isEEA?: boolean;
  nationalIdentifierName?: string;
  taxIdentifierName?: string;
  taxIdentifierShortName?: string;
  taxIdentifierPlaceholder?: string;
  isUsingSeparateTIN: boolean;
  showInCountrySelector: boolean;
  showInEuOnboarding: boolean;
  hideInNationality?: boolean;
  region?: CountryRegion;
  status: CountryStatus;
  legalEntities?: readonly LegalEntity[];
  instrumentsUniverses?: readonly InstrumentsUniverses[];
  phoneNumberPlaceholder?: string;
  taxNumberFlow?: 'deferred_submission' | 'skippable' | 'required';
};

export const countryDataByCode: Record<SupportedCountryCodes, CountryData> = {
  AF: {
    code: 'AF',
    dialCode: '+93',
    emoji: '🇦🇫',
    name: 'Afghanistan',
    adjective: 'Afghan',
    code3: 'AFG',
    currency: 'AFN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AX: {
    code: 'AX',
    dialCode: '+358',
    emoji: '🇦🇽',
    name: 'Åland Islands',
    adjective: 'Åland Islands',
    code3: 'ALA',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AL: {
    code: 'AL',
    dialCode: '+355',
    emoji: '🇦🇱',
    name: 'Albania',
    adjective: 'Albanian',
    code3: 'ALB',
    currency: 'ALL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  DZ: {
    code: 'DZ',
    dialCode: '+213',
    emoji: '🇩🇿',
    name: 'Algeria',
    adjective: 'Algerian',
    code3: 'DZA',
    currency: 'DZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AD: {
    code: 'AD',
    dialCode: '+376',
    emoji: '🇦🇩',
    name: 'Andorra',
    adjective: 'Andorran',
    code3: 'AND',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AO: {
    code: 'AO',
    dialCode: '+244',
    emoji: '🇦🇴',
    name: 'Angola',
    adjective: 'Angolan',
    code3: 'AGO',
    currency: 'AOA',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AG: {
    code: 'AG',
    dialCode: '+1268',
    emoji: '🇦🇬',
    name: 'Antigua & Barbuda',
    adjective: 'Antiguans',
    code3: 'ATG',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AR: {
    code: 'AR',
    dialCode: '+54',
    emoji: '🇦🇷',
    name: 'Argentina',
    adjective: 'Argentinean',
    code3: 'ARG',
    currency: 'ARS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AM: {
    code: 'AM',
    dialCode: '+374',
    emoji: '🇦🇲',
    name: 'Armenia',
    adjective: 'Armenian',
    code3: 'ARM',
    currency: 'AMD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AU: {
    code: 'AU',
    dialCode: '+61',
    emoji: '🇦🇺',
    name: 'Australia',
    adjective: 'Australian',
    code3: 'AUS',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AT: {
    code: 'AT',
    dialCode: '+43',
    emoji: '🇦🇹',
    name: 'Austria',
    adjective: 'Austrian',
    code3: 'AUT',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'VAT number (ATU number)',
    taxIdentifierShortName: 'ATU',
    taxIdentifierPlaceholder: '000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  AZ: {
    code: 'AZ',
    dialCode: '+994',
    emoji: '🇦🇿',
    name: 'Azerbaijan',
    adjective: 'Azerbaijani',
    code3: 'AZE',
    currency: 'AZN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BS: {
    code: 'BS',
    dialCode: '+1242',
    emoji: '🇧🇸',
    name: 'Bahamas',
    adjective: 'Bahamian',
    code3: 'BHS',
    currency: 'BSD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BH: {
    code: 'BH',
    dialCode: '+973',
    emoji: '🇧🇭',
    name: 'Bahrain',
    adjective: 'Bahraini',
    code3: 'BHR',
    currency: 'BHD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BD: {
    code: 'BD',
    dialCode: '+880',
    emoji: '🇧🇩',
    name: 'Bangladesh',
    adjective: 'Bangladeshi',
    code3: 'BGD',
    currency: 'BDT',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BB: {
    code: 'BB',
    dialCode: '+1246',
    emoji: '🇧🇧',
    name: 'Barbados',
    adjective: 'Barbadian',
    code3: 'BRB',
    currency: 'BBD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BY: {
    code: 'BY',
    dialCode: '+375',
    emoji: '🇧🇾',
    name: 'Belarus',
    adjective: 'Belarusian',
    code3: 'BLR',
    currency: 'BYN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BE: {
    code: 'BE',
    dialCode: '+32',
    emoji: '🇧🇪',
    name: 'Belgium',
    adjective: 'Belgian',
    code3: 'BEL',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName:
      'Belgian National Number (Numéro de registre national – Rijksregisternummer)',
    taxIdentifierName: 'Numéro de registre national – Rijksregisternummer (NN)',
    taxIdentifierShortName: 'NN',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda', 'sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS', 'CRYPTO'],
    phoneNumberPlaceholder: '12 34 56 78',
    taxNumberFlow: 'required',
  },
  BZ: {
    code: 'BZ',
    dialCode: '+501',
    emoji: '🇧🇿',
    name: 'Belize',
    adjective: 'Belizean',
    code3: 'BLZ',
    currency: 'BZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BJ: {
    code: 'BJ',
    dialCode: '+229',
    emoji: '🇧🇯',
    name: 'Benin',
    adjective: 'Beninese',
    code3: 'BEN',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BL: {
    code: 'BL',
    dialCode: '+590',
    emoji: '🇧🇱',
    name: 'Saint Barthélemy',
    adjective: 'Saint Barthélemy',
    code3: 'BLM',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: true,
    status: 'HIDDEN',
  },
  BM: {
    code: 'BM',
    dialCode: '+1441',
    emoji: '🇧🇲',
    name: 'Bermuda',
    adjective: 'Bermuda',
    code3: 'BMU',
    currency: 'BMD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BT: {
    code: 'BT',
    dialCode: '+975',
    emoji: '🇧🇹',
    name: 'Bhutan',
    adjective: 'Bhutanese',
    code3: 'BTN',
    currency: 'BTN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BO: {
    code: 'BO',
    dialCode: '+591',
    emoji: '🇧🇴',
    name: 'Bolivia',
    adjective: 'Bolivian',
    code3: 'BOL',
    currency: 'BOB',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BA: {
    code: 'BA',
    dialCode: '+387',
    emoji: '🇧🇦',
    name: 'Bosnia & Herzegovina',
    adjective: 'Bosnian - Herzegovinian',
    code3: 'BIH',
    currency: 'BAM',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BW: {
    code: 'BW',
    dialCode: '+267',
    emoji: '🇧🇼',
    name: 'Botswana',
    adjective: 'Batswana',
    code3: 'BWA',
    currency: 'BWP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BR: {
    code: 'BR',
    dialCode: '+55',
    emoji: '🇧🇷',
    name: 'Brazil',
    adjective: 'Brazilian',
    code3: 'BRA',
    currency: 'BRL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BN: {
    code: 'BN',
    dialCode: '+673',
    emoji: '🇧🇳',
    name: 'Brunei',
    adjective: 'Bruneian',
    code3: 'BRN',
    currency: 'BND',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BG: {
    code: 'BG',
    dialCode: '+359',
    emoji: '🇧🇬',
    name: 'Bulgaria',
    adjective: 'Bulgarian',
    code3: 'BGR',
    currency: 'BGN',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Bulgarian Personal Number',
    taxIdentifierName:
      'Unified Civil Number (Official number or Personal Number of a Foreigner for foreign citizens)',
    taxIdentifierPlaceholder: '0000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  BF: {
    code: 'BF',
    dialCode: '+226',
    emoji: '🇧🇫',
    name: 'Burkina Faso',
    adjective: 'Burkinabe',
    code3: 'BFA',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  BI: {
    code: 'BI',
    dialCode: '+257',
    emoji: '🇧🇮',
    name: 'Burundi',
    adjective: 'Burundian',
    code3: 'BDI',
    currency: 'BIF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KH: {
    code: 'KH',
    dialCode: '+855',
    emoji: '🇰🇭',
    name: 'Cambodia',
    adjective: 'Cambodian',
    code3: 'KHM',
    currency: 'KHR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CM: {
    code: 'CM',
    dialCode: '+237',
    emoji: '🇨🇲',
    name: 'Cameroon',
    adjective: 'Cameroonian',
    code3: 'CMR',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CA: {
    code: 'CA',
    dialCode: '+1',
    emoji: '🇨🇦',
    name: 'Canada',
    adjective: 'Canadian',
    code3: 'CAN',
    currency: 'CAD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CV: {
    code: 'CV',
    dialCode: '+238',
    emoji: '🇨🇻',
    name: 'Cabo Verde',
    adjective: 'Cape Verdean',
    code3: 'CPV',
    currency: 'CVE',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KY: {
    code: 'KY',
    dialCode: '+1345',
    emoji: '🇰🇾',
    name: 'Cayman Islands',
    adjective: 'Cayman Islands',
    code3: 'CYM',
    currency: 'KYD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CF: {
    code: 'CF',
    dialCode: '+236',
    emoji: '🇨🇫',
    name: 'Central African Republic',
    adjective: 'Central African',
    code3: 'CAF',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TD: {
    code: 'TD',
    dialCode: '+235',
    emoji: '🇹🇩',
    name: 'Chad',
    adjective: 'Chadian',
    code3: 'TCD',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CL: {
    code: 'CL',
    dialCode: '+56',
    emoji: '🇨🇱',
    name: 'Chile',
    adjective: 'Chilean',
    code3: 'CHL',
    currency: 'CLP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CN: {
    code: 'CN',
    dialCode: '+86',
    emoji: '🇨🇳',
    name: 'China',
    adjective: 'Chinese',
    code3: 'CHN',
    currency: 'CNY',
    symbol: '¥',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CX: {
    code: 'CX',
    dialCode: '+61',
    emoji: '🇨🇽',
    name: 'Christmas Island',
    adjective: 'Christmas Island',
    code3: 'CXR',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CC: {
    code: 'CC',
    dialCode: '+61',
    emoji: '🇨🇨',
    name: 'Cocos (Keeling) Islands',
    adjective: 'Cocos (Keeling) Islands',
    code3: 'CCK',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CO: {
    code: 'CO',
    dialCode: '+57',
    emoji: '🇨🇴',
    name: 'Colombia',
    adjective: 'Colombian',
    code3: 'COL',
    currency: 'COP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KM: {
    code: 'KM',
    dialCode: '+269',
    emoji: '🇰🇲',
    name: 'Comoros',
    adjective: 'Comoran',
    code3: 'COM',
    currency: 'KMF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CG: {
    code: 'CG',
    dialCode: '+242',
    emoji: '🇨🇬',
    name: 'Congo (Brazzaville)',
    adjective: 'Congolese',
    code3: 'COG',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CD: {
    code: 'CD',
    dialCode: '+243',
    emoji: '🇨🇩',
    name: 'Congo - Kinshasa',
    adjective: 'Congolese',
    code3: 'COD',
    currency: 'CDF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CK: {
    code: 'CK',
    dialCode: '+682',
    emoji: '🇨🇰',
    name: 'Cook Islands',
    adjective: 'Cook Islands',
    code3: 'COK',
    currency: 'NZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CR: {
    code: 'CR',
    dialCode: '+506',
    emoji: '🇨🇷',
    name: 'Costa Rica',
    adjective: 'Costa Rican',
    code3: 'CRI',
    currency: 'CRC',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CI: {
    code: 'CI',
    dialCode: '+225',
    emoji: '🇨🇮',
    name: 'Ivory Coast',
    adjective: 'Ivorian',
    code3: 'CIV',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  HR: {
    code: 'HR',
    dialCode: '+385',
    emoji: '🇭🇷',
    name: 'Croatia',
    adjective: 'Croatian',
    code3: 'HRV',
    currency: 'HRK',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal Identification Number (OIB – Osobni identifikacijski broj)',
    taxIdentifierName: 'Personal Identification Number (OIB – Osobni identifikacijski broj)',
    taxIdentifierShortName: 'OIB',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  CU: {
    code: 'CU',
    dialCode: '+53',
    emoji: '🇨🇺',
    name: 'Cuba',
    adjective: 'Cuban',
    code3: 'CUB',
    currency: 'CUP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  CY: {
    code: 'CY',
    dialCode: '+357',
    emoji: '🇨🇾',
    name: 'Cyprus',
    adjective: 'Cypriot',
    code3: 'CYP',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    taxIdentifierName: 'Cyprus Tax Number (TIC)',
    taxIdentifierShortName: 'TIC',
    taxIdentifierPlaceholder: '00000000A',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  CZ: {
    code: 'CZ',
    dialCode: '+420',
    emoji: '🇨🇿',
    name: 'Czechia',
    adjective: 'Czech',
    code3: 'CZE',
    currency: 'CZK',
    isEEA: true,
    showInEuOnboarding: false,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'National identification number (Rodné číslo)',
    taxIdentifierName: 'National identification number (Rodné číslo)',
    taxIdentifierPlaceholder: '000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
  },
  DK: {
    code: 'DK',
    dialCode: '+45',
    emoji: '🇩🇰',
    name: 'Denmark',
    adjective: 'Danish',
    code3: 'DNK',
    currency: 'DKK',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal identity code 10 digits alphanumerical: DDMMYYXXXX',
    taxIdentifierName: 'Danish CPR (Central Person Registration) number',
    taxIdentifierShortName: 'CPR',
    taxIdentifierPlaceholder: '0000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  DJ: {
    code: 'DJ',
    dialCode: '+253',
    emoji: '🇩🇯',
    name: 'Djibouti',
    adjective: 'Djibouti',
    code3: 'DJI',
    currency: 'DJF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  DM: {
    code: 'DM',
    dialCode: '+1767',
    emoji: '🇩🇲',
    name: 'Dominica',
    adjective: 'Dominican',
    code3: 'DMA',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  DO: {
    code: 'DO',
    dialCode: '+1849',
    emoji: '🇩🇴',
    name: 'Dominican Republic',
    adjective: 'Dominican',
    code3: 'DOM',
    currency: 'DOP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  EC: {
    code: 'EC',
    dialCode: '+593',
    emoji: '🇪🇨',
    name: 'Ecuador',
    adjective: 'Ecuadorean',
    code3: 'ECU',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  EG: {
    code: 'EG',
    dialCode: '+20',
    emoji: '🇪🇬',
    name: 'Egypt',
    adjective: 'Egyptian',
    code3: 'EGY',
    currency: 'EGP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SV: {
    code: 'SV',
    dialCode: '+503',
    emoji: '🇸🇻',
    name: 'El Salvador',
    adjective: 'Salvadoran',
    code3: 'SLV',
    currency: 'SVC',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GF: {
    code: 'GF',
    dialCode: '+594',
    emoji: '🇫🇷',
    name: 'French Guiana',
    adjective: 'Guianese',
    code3: 'GUF',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: false,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  GQ: {
    code: 'GQ',
    dialCode: '+240',
    emoji: '🇬🇶',
    name: 'Equatorial Guinea',
    adjective: 'Equatorial Guinean',
    code3: 'GNQ',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GP: {
    code: 'GP',
    dialCode: '+590',
    emoji: '🇫🇷',
    name: 'Guadeloupe',
    adjective: 'Guadeloupean',
    code3: 'GLP',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: false,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  ER: {
    code: 'ER',
    dialCode: '+291',
    emoji: '🇪🇷',
    name: 'Eritrea',
    adjective: 'Eritrean',
    code3: 'ERI',
    currency: 'ERN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  EE: {
    code: 'EE',
    dialCode: '+372',
    emoji: '🇪🇪',
    name: 'Estonia',
    adjective: 'Estonian',
    code3: 'EST',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Estonian Personal Identification Code (Isikukood)',
    taxIdentifierName: 'Estonian personal identification number (Isikukood)',
    taxIdentifierPlaceholder: '10000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  ET: {
    code: 'ET',
    dialCode: '+251',
    emoji: '🇪🇹',
    name: 'Ethiopia',
    adjective: 'Ethiopian',
    code3: 'ETH',
    currency: 'ETB',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  FK: {
    code: 'FK',
    dialCode: '+500',
    emoji: '🇫🇰',
    name: 'Falkland Islands',
    adjective: 'Falkland Islands (Malvinas)',
    code3: 'FLK',
    currency: 'FKP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  FO: {
    code: 'FO',
    dialCode: '+298',
    emoji: '🇫🇴',
    name: 'Faroe Islands',
    adjective: 'Faroe Islands',
    code3: 'FRO',
    currency: 'DKK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  FJ: {
    code: 'FJ',
    dialCode: '+679',
    emoji: '🇫🇯',
    name: 'Fiji',
    adjective: 'Fijian',
    code3: 'FJI',
    currency: 'FJD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  FI: {
    code: 'FI',
    dialCode: '+358',
    emoji: '🇫🇮',
    name: 'Finland',
    adjective: 'Finnish',
    code3: 'FIN',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal identity code',
    taxIdentifierName: 'Finnish Social Security number',
    taxIdentifierPlaceholder: '000000+000A',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  FR: {
    code: 'FR',
    dialCode: '+33',
    emoji: '🇫🇷',
    name: 'France',
    adjective: 'French',
    code3: 'FRA',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'Numéro fiscal de référence (NIF)',
    taxIdentifierShortName: 'NIF',
    taxIdentifierPlaceholder: '0000000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda', 'sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS', 'CRYPTO'],
    phoneNumberPlaceholder: '1 23 45 67 89',
    taxNumberFlow: 'skippable',
  },
  PF: {
    code: 'PF',
    dialCode: '+689',
    emoji: '🇵🇫',
    name: 'French Polynesia',
    adjective: 'French',
    code3: 'PYF',
    currency: 'XPF',
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: true,
    status: 'HIDDEN',
  },
  GA: {
    code: 'GA',
    dialCode: '+241',
    emoji: '🇬🇦',
    name: 'Gabon',
    adjective: 'Gabonese',
    code3: 'GAB',
    currency: 'XAF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GM: {
    code: 'GM',
    dialCode: '+220',
    emoji: '🇬🇲',
    name: 'Gambia',
    adjective: 'Gambian',
    code3: 'GMB',
    currency: 'GMD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GE: {
    code: 'GE',
    dialCode: '+995',
    emoji: '🇬🇪',
    name: 'Georgia',
    adjective: 'Georgian',
    code3: 'GEO',
    currency: 'GEL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  DE: {
    code: 'DE',
    dialCode: '+49',
    emoji: '🇩🇪',
    name: 'Germany',
    adjective: 'German',
    code3: 'DEU',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'Tax Identification Number',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS'],
    taxNumberFlow: 'skippable',
  },
  GH: {
    code: 'GH',
    dialCode: '+233',
    emoji: '🇬🇭',
    name: 'Ghana',
    adjective: 'Ghanaian',
    code3: 'GHA',
    currency: 'GHS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GI: {
    code: 'GI',
    dialCode: '+350',
    emoji: '🇬🇮',
    name: 'Gibraltar',
    adjective: 'Gibraltar',
    code3: 'GIB',
    currency: 'GIP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GR: {
    code: 'GR',
    dialCode: '+30',
    emoji: '🇬🇷',
    name: 'Greece',
    adjective: 'Greek',
    code3: 'GRC',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: '10 DSS digit investor share',
    taxIdentifierName: 'Unique taxpayer identification number / TIN',
    taxIdentifierShortName: 'TIN',
    taxIdentifierPlaceholder: '0000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  GL: {
    code: 'GL',
    dialCode: '+299',
    emoji: '🇬🇱',
    name: 'Greenland',
    adjective: 'Greenland',
    code3: 'GRL',
    currency: 'DKK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GD: {
    code: 'GD',
    dialCode: '+1473',
    emoji: '🇬🇩',
    name: 'Grenada',
    adjective: 'Grenadian',
    code3: 'GRD',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GU: {
    code: 'GU',
    dialCode: '+1671',
    emoji: '🇬🇺',
    name: 'Guam',
    adjective: 'Guam',
    code3: 'GUM',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GT: {
    code: 'GT',
    dialCode: '+502',
    emoji: '🇬🇹',
    name: 'Guatemala',
    adjective: 'Guatemalan',
    code3: 'GTM',
    currency: 'GTQ',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GG: {
    code: 'GG',
    dialCode: '+44',
    emoji: '🇬🇬',
    name: 'Guernsey',
    adjective: 'Guernsey',
    code3: 'GGY',
    currency: 'GBP',
    symbol: '£',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GN: {
    code: 'GN',
    dialCode: '+224',
    emoji: '🇬🇳',
    name: 'Guinea',
    adjective: 'Guinean',
    code3: 'GIN',
    currency: 'GNF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GW: {
    code: 'GW',
    dialCode: '+245',
    emoji: '🇬🇼',
    name: 'Guinea-Bissau',
    adjective: 'Guinea-Bissauan',
    code3: 'GNB',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GY: {
    code: 'GY',
    dialCode: '+592',
    emoji: '🇬🇾',
    name: 'Guyana',
    adjective: 'Guyanese',
    code3: 'GUY',
    currency: 'GYD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  HT: {
    code: 'HT',
    dialCode: '+509',
    emoji: '🇭🇹',
    name: 'Haiti',
    adjective: 'Haitian',
    code3: 'HTI',
    currency: 'HTG',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VA: {
    code: 'VA',
    dialCode: '+379',
    emoji: '🇻🇦',
    name: 'Vatican City',
    adjective: 'Holy See (Vatican City State)',
    code3: 'VAT',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  HN: {
    code: 'HN',
    dialCode: '+504',
    emoji: '🇭🇳',
    name: 'Honduras',
    adjective: 'Honduran',
    code3: 'HND',
    currency: 'HNL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  HK: {
    code: 'HK',
    dialCode: '+852',
    emoji: '🇭🇰',
    name: 'Hong Kong SAR China',
    adjective: 'Hong Kong',
    code3: 'HKG',
    currency: 'HKD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  HU: {
    code: 'HU',
    dialCode: '+36',
    emoji: '🇭🇺',
    name: 'Hungary',
    adjective: 'Hungarian',
    code3: 'HUN',
    currency: 'HUF',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'Adóazonosító jel',
    taxIdentifierPlaceholder: '8000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    taxNumberFlow: 'required',
  },
  IS: {
    code: 'IS',
    dialCode: '+354',
    emoji: '🇮🇸',
    name: 'Iceland',
    adjective: 'Icelandic',
    code3: 'ISL',
    currency: 'ISK',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal Identity Code (Kennitala)',
    taxIdentifierName: 'Personal Identity Code (Kennitala)',
    taxIdentifierPlaceholder: '0000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  IN: {
    code: 'IN',
    dialCode: '+91',
    emoji: '🇮🇳',
    name: 'India',
    adjective: 'Indian',
    code3: 'IND',
    currency: 'INR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ID: {
    code: 'ID',
    dialCode: '+62',
    emoji: '🇮🇩',
    name: 'Indonesia',
    adjective: 'Indonesian',
    code3: 'IDN',
    currency: 'IDR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  IR: {
    code: 'IR',
    dialCode: '+98',
    emoji: '🇮🇷',
    name: 'Iran',
    adjective: 'Iranian',
    code3: 'IRN',
    currency: 'IRR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  IQ: {
    code: 'IQ',
    dialCode: '+964',
    emoji: '🇮🇶',
    name: 'Iraq',
    adjective: 'Iraqi',
    code3: 'IRQ',
    currency: 'IQD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  IE: {
    code: 'IE',
    dialCode: '+353',
    emoji: '🇮🇪',
    name: 'Ireland',
    adjective: 'Irish',
    code3: 'IRL',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'Personal Public Service Number (PPS No)',
    taxIdentifierShortName: 'PPS',
    taxIdentifierPlaceholder: '0000000A',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  IM: {
    code: 'IM',
    dialCode: '+44',
    emoji: '🇮🇲',
    name: 'Isle of Man',
    adjective: 'Isle of Man',
    code3: 'IMN',
    currency: 'GBP',
    symbol: '£',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  IL: {
    code: 'IL',
    dialCode: '+972',
    emoji: '🇮🇱',
    name: 'Israel',
    adjective: 'Israeli',
    code3: 'ISR',
    currency: 'ILS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  IT: {
    code: 'IT',
    dialCode: '+39',
    emoji: '🇮🇹',
    name: 'Italy',
    adjective: 'Italian',
    code3: 'ITA',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Fiscal code (Codice fiscale)',
    taxIdentifierName: 'Codice Fiscale',
    taxIdentifierPlaceholder: 'AAAAAA00A00A000A',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS'],
    taxNumberFlow: 'required',
  },
  JM: {
    code: 'JM',
    dialCode: '+1876',
    emoji: '🇯🇲',
    name: 'Jamaica',
    adjective: 'Jamaican',
    code3: 'JAM',
    currency: 'JMD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  JP: {
    code: 'JP',
    dialCode: '+81',
    emoji: '🇯🇵',
    name: 'Japan',
    adjective: 'Japanese',
    code3: 'JPN',
    currency: 'JPY',
    symbol: '¥',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  JE: {
    code: 'JE',
    dialCode: '+44',
    emoji: '🇯🇪',
    name: 'Jersey',
    adjective: 'Jersey',
    code3: 'JEY',
    currency: 'GBP',
    symbol: '£',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  JO: {
    code: 'JO',
    dialCode: '+962',
    emoji: '🇯🇴',
    name: 'Jordan',
    adjective: 'Jordanian',
    code3: 'JOR',
    currency: 'JOD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KZ: {
    code: 'KZ',
    dialCode: '+77',
    emoji: '🇰🇿',
    name: 'Kazakhstan',
    adjective: 'Kazakhstani',
    code3: 'KAZ',
    currency: 'KZT',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KE: {
    code: 'KE',
    dialCode: '+254',
    emoji: '🇰🇪',
    name: 'Kenya',
    adjective: 'Kenyan',
    code3: 'KEN',
    currency: 'KES',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KI: {
    code: 'KI',
    dialCode: '+686',
    emoji: '🇰🇮',
    name: 'Kiribati',
    adjective: 'I-Kiribati',
    code3: 'KIR',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KP: {
    code: 'KP',
    dialCode: '+850',
    emoji: '🇰🇵',
    name: 'North Korea',
    adjective: 'North Korean',
    code3: 'PRK',
    currency: 'KPW',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KR: {
    code: 'KR',
    dialCode: '+82',
    emoji: '🇰🇷',
    name: 'South Korea',
    adjective: 'South Korean',
    code3: 'KOR',
    currency: 'KRW',
    symbol: '₩',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KW: {
    code: 'KW',
    dialCode: '+965',
    emoji: '🇰🇼',
    name: 'Kuwait',
    adjective: 'Kuwaiti',
    code3: 'KWT',
    currency: 'KWD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KG: {
    code: 'KG',
    dialCode: '+996',
    emoji: '🇰🇬',
    name: 'Kyrgyzstan',
    adjective: 'Kyrgyz',
    code3: 'KGZ',
    currency: 'KGS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LA: {
    code: 'LA',
    dialCode: '+856',
    emoji: '🇱🇦',
    name: 'Laos',
    adjective: 'Laotian',
    code3: 'LAO',
    currency: 'LAK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LV: {
    code: 'LV',
    dialCode: '+371',
    emoji: '🇱🇻',
    name: 'Latvia',
    adjective: 'Latvian',
    code3: 'LVA',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal code (Personas kods)',
    taxIdentifierName: 'Personal Identity Number',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  LB: {
    code: 'LB',
    dialCode: '+961',
    emoji: '🇱🇧',
    name: 'Lebanon',
    adjective: 'Lebanese',
    code3: 'LBN',
    currency: 'LBP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LS: {
    code: 'LS',
    dialCode: '+266',
    emoji: '🇱🇸',
    name: 'Lesotho',
    adjective: 'Mosotho',
    code3: 'LSO',
    currency: 'LSL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LR: {
    code: 'LR',
    dialCode: '+231',
    emoji: '🇱🇷',
    name: 'Liberia',
    adjective: 'Liberian',
    code3: 'LBR',
    currency: 'LRD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LY: {
    code: 'LY',
    dialCode: '+218',
    emoji: '🇱🇾',
    name: 'Libya',
    adjective: 'Libyan',
    code3: 'LBY',
    currency: 'LYD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LI: {
    code: 'LI',
    dialCode: '+423',
    emoji: '🇱🇮',
    name: 'Liechtenstein',
    adjective: 'Liechtensteiner',
    code3: 'LIE',
    currency: 'CHF',
    symbol: 'Fr',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    taxIdentifierName: 'Personenidentifikationsnummer (PEID Nummer)',
    taxIdentifierShortName: 'PEID',
    taxIdentifierPlaceholder: '000000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LT: {
    code: 'LT',
    dialCode: '+370',
    emoji: '🇱🇹',
    name: 'Lithuania',
    adjective: 'Lithuanian',
    code3: 'LTU',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal code (Asmens kodas)',
    taxIdentifierName: 'Personal code (Asmens kodas)',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  LU: {
    code: 'LU',
    dialCode: '+352',
    emoji: '🇱🇺',
    name: 'Luxembourg',
    adjective: 'Luxembourger',
    code3: 'LUX',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    taxIdentifierName: 'Numéro d’identification personnelle',
    taxIdentifierPlaceholder: '0000000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS'],
    taxNumberFlow: 'skippable',
  },
  MF: {
    code: 'MF',
    dialCode: '+590',
    emoji: '🇫🇷',
    name: 'Saint Martin (French part)',
    adjective: 'Saint-Martinois',
    code3: 'MAF',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: true,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  MO: {
    code: 'MO',
    dialCode: '+853',
    emoji: '🇲🇴',
    name: 'Macau SAR China',
    adjective: 'Macao',
    code3: 'MAC',
    currency: 'MOP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MK: {
    code: 'MK',
    dialCode: '+389',
    emoji: '🇲🇰',
    name: 'North Macedonia',
    adjective: 'Macedonian',
    code3: 'MKD',
    currency: 'MKD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MG: {
    code: 'MG',
    dialCode: '+261',
    emoji: '🇲🇬',
    name: 'Madagascar',
    adjective: 'Malagasy',
    code3: 'MDG',
    currency: 'MGA',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MQ: {
    code: 'MQ',
    dialCode: '+596',
    emoji: '🇫🇷',
    name: 'Martinique',
    adjective: 'Martinican',
    code3: 'MTQ',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: false,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  MW: {
    code: 'MW',
    dialCode: '+265',
    emoji: '🇲🇼',
    name: 'Malawi',
    adjective: 'Malawian',
    code3: 'MWI',
    currency: 'MWK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MY: {
    code: 'MY',
    dialCode: '+60',
    emoji: '🇲🇾',
    name: 'Malaysia',
    adjective: 'Malaysian',
    code3: 'MYS',
    currency: 'MYR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MV: {
    code: 'MV',
    dialCode: '+960',
    emoji: '🇲🇻',
    name: 'Maldives',
    adjective: 'Maldivan',
    code3: 'MDV',
    currency: 'MVR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ML: {
    code: 'ML',
    dialCode: '+223',
    emoji: '🇲🇱',
    name: 'Mali',
    adjective: 'Malian',
    code3: 'MLI',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MT: {
    code: 'MT',
    dialCode: '+356',
    emoji: '🇲🇹',
    name: 'Malta',
    adjective: 'Maltese',
    code3: 'MLT',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'National Identification Number',
    taxIdentifierName: 'Identity Card Number',
    taxIdentifierPlaceholder: '0000000A',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  MH: {
    code: 'MH',
    dialCode: '+692',
    emoji: '🇲🇭',
    name: 'Marshall Islands',
    adjective: 'Marshallese',
    code3: 'MHL',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MR: {
    code: 'MR',
    dialCode: '+222',
    emoji: '🇲🇷',
    name: 'Mauritania',
    adjective: 'Mauritanian',
    code3: 'MRT',
    currency: 'MRO',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MU: {
    code: 'MU',
    dialCode: '+230',
    emoji: '🇲🇺',
    name: 'Mauritius',
    adjective: 'Mauritian',
    code3: 'MUS',
    currency: 'MUR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MX: {
    code: 'MX',
    dialCode: '+52',
    emoji: '🇲🇽',
    name: 'Mexico',
    adjective: 'Mexican',
    code3: 'MEX',
    currency: 'MXN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ME: {
    code: 'ME',
    dialCode: '+382',
    emoji: '🇲🇪',
    name: 'Montenegro',
    adjective: 'Montenegrin',
    code3: 'MNE',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  FM: {
    code: 'FM',
    dialCode: '+691',
    emoji: '🇫🇲',
    name: 'Micronesia',
    adjective: 'Micronesian',
    code3: 'FSM',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MD: {
    code: 'MD',
    dialCode: '+373',
    emoji: '🇲🇩',
    name: 'Moldova',
    adjective: 'Moldovan',
    code3: 'MDA',
    currency: 'MDL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MC: {
    code: 'MC',
    dialCode: '+377',
    emoji: '🇲🇨',
    name: 'Monaco',
    adjective: 'Monacan',
    code3: 'MCO',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MN: {
    code: 'MN',
    dialCode: '+976',
    emoji: '🇲🇳',
    name: 'Mongolia',
    adjective: 'Mongolian',
    code3: 'MNG',
    currency: 'MNT',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MS: {
    code: 'MS',
    dialCode: '+1664',
    emoji: '🇲🇸',
    name: 'Montserrat',
    adjective: 'Montserrat',
    code3: 'MSR',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MA: {
    code: 'MA',
    dialCode: '+212',
    emoji: '🇲🇦',
    name: 'Morocco',
    adjective: 'Moroccan',
    code3: 'MAR',
    currency: 'MAD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MZ: {
    code: 'MZ',
    dialCode: '+258',
    emoji: '🇲🇿',
    name: 'Mozambique',
    adjective: 'Mozambican',
    code3: 'MOZ',
    currency: 'MZN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MM: {
    code: 'MM',
    dialCode: '+95',
    emoji: '🇲🇲',
    name: 'Myanmar (Burma)',
    adjective: 'Burmese',
    code3: 'MMR',
    currency: 'MMK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NA: {
    code: 'NA',
    dialCode: '+264',
    emoji: '🇳🇦',
    name: 'Namibia',
    adjective: 'Namibian',
    code3: 'NAM',
    currency: 'NAD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NR: {
    code: 'NR',
    dialCode: '+674',
    emoji: '🇳🇷',
    name: 'Nauru',
    adjective: 'Nauruan',
    code3: 'NRU',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NP: {
    code: 'NP',
    dialCode: '+977',
    emoji: '🇳🇵',
    name: 'Nepal',
    adjective: 'Nepalese',
    code3: 'NPL',
    currency: 'NPR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NL: {
    code: 'NL',
    dialCode: '+31',
    emoji: '🇳🇱',
    name: 'Netherlands',
    adjective: 'Dutch',
    code3: 'NLD',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    taxIdentifierName: 'Persoonsnummer (BSN)',
    taxIdentifierShortName: 'BSN',
    taxIdentifierPlaceholder: '000000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS'],
    taxNumberFlow: 'skippable',
  },
  NC: {
    code: 'NC',
    dialCode: '+687',
    emoji: '🇳🇨',
    name: 'New Caledonia',
    adjective: 'New Caledonia',
    code3: 'NCL',
    currency: 'XPF',
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: true,
    status: 'HIDDEN',
  },
  NZ: {
    code: 'NZ',
    dialCode: '+64',
    emoji: '🇳🇿',
    name: 'New Zealand',
    adjective: 'New Zealander',
    code3: 'NZL',
    currency: 'NZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NI: {
    code: 'NI',
    dialCode: '+505',
    emoji: '🇳🇮',
    name: 'Nicaragua',
    adjective: 'Nicaraguan',
    code3: 'NIC',
    currency: 'NIO',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NE: {
    code: 'NE',
    dialCode: '+227',
    emoji: '🇳🇪',
    name: 'Niger',
    adjective: 'Nigerien',
    code3: 'NER',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NG: {
    code: 'NG',
    dialCode: '+234',
    emoji: '🇳🇬',
    name: 'Nigeria',
    adjective: 'Nigerian',
    code3: 'NGA',
    currency: 'NGN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NU: {
    code: 'NU',
    dialCode: '+683',
    emoji: '🇳🇺',
    name: 'Niue',
    adjective: 'Niue',
    code3: 'NIU',
    currency: 'NZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NF: {
    code: 'NF',
    dialCode: '+672',
    emoji: '🇳🇫',
    name: 'Norfolk Island',
    adjective: 'Norfolk Island',
    code3: 'NFK',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  MP: {
    code: 'MP',
    dialCode: '+1670',
    emoji: '🇲🇵',
    name: 'Northern Mariana Islands',
    adjective: 'Northern Mariana Islands',
    code3: 'MNP',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  NO: {
    code: 'NO',
    dialCode: '+47',
    emoji: '🇳🇴',
    name: 'Norway',
    adjective: 'Norwegian',
    code3: 'NOR',
    currency: 'NOK',
    symbol: 'Kr',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: '11 digit personal id (Foedselsnummer)',
    taxIdentifierName: 'National identity numbers (Foedselsnummer) or D-numbers ',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'HIDDEN',
  },
  OM: {
    code: 'OM',
    dialCode: '+968',
    emoji: '🇴🇲',
    name: 'Oman',
    adjective: 'Omani',
    code3: 'OMN',
    currency: 'OMR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PK: {
    code: 'PK',
    dialCode: '+92',
    emoji: '🇵🇰',
    name: 'Pakistan',
    adjective: 'Pakistani',
    code3: 'PAK',
    currency: 'PKR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PW: {
    code: 'PW',
    dialCode: '+680',
    emoji: '🇵🇼',
    name: 'Palau',
    adjective: 'Palauan',
    code3: 'PLW',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PS: {
    code: 'PS',
    dialCode: '+970',
    emoji: '🇵🇸',
    name: 'Palestinian Territories',
    adjective: 'Palestinian Territory, Occupied',
    code3: 'PSE',
    currency: 'ILS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PA: {
    code: 'PA',
    dialCode: '+507',
    emoji: '🇵🇦',
    name: 'Panama',
    adjective: 'Panamanian',
    code3: 'PAN',
    currency: 'PAB',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PG: {
    code: 'PG',
    dialCode: '+675',
    emoji: '🇵🇬',
    name: 'Papua New Guinea',
    adjective: 'Papua New Guinea',
    code3: 'PNG',
    currency: 'PGK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PY: {
    code: 'PY',
    dialCode: '+595',
    emoji: '🇵🇾',
    name: 'Paraguay',
    adjective: 'Paraguayan',
    code3: 'PRY',
    currency: 'PYG',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PE: {
    code: 'PE',
    dialCode: '+51',
    emoji: '🇵🇪',
    name: 'Peru',
    adjective: 'Peruvian',
    code3: 'PER',
    currency: 'PEN',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PH: {
    code: 'PH',
    dialCode: '+63',
    emoji: '🇵🇭',
    name: 'Philippines',
    adjective: 'Filipino',
    code3: 'PHL',
    currency: 'PHP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  PL: {
    code: 'PL',
    dialCode: '+48',
    emoji: '🇵🇱',
    name: 'Poland',
    adjective: 'Polish',
    code3: 'POL',
    currency: 'PLN',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'National Identification Number (PESEL)',
    taxIdentifierName: 'PESEL',
    taxIdentifierShortName: 'PESEL',
    taxIdentifierPlaceholder: '00000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda', 'sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS', 'CRYPTO'],
    taxNumberFlow: 'required',
  },
  PT: {
    code: 'PT',
    dialCode: '+351',
    emoji: '🇵🇹',
    name: 'Portugal',
    adjective: 'Portuguese',
    code3: 'PRT',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Tax number (Número de Identificação Fiscal)',
    taxIdentifierName: 'Número de Identificação Fiscal (NIF)',
    taxIdentifierShortName: 'NIF',
    taxIdentifierPlaceholder: '000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda', 'sfa'],
    instrumentsUniverses: ['US-STOCKS', 'EU-STOCKS', 'CRYPTO'],
    taxNumberFlow: 'required',
  },
  PR: {
    code: 'PR',
    dialCode: '+1939',
    emoji: '🇵🇷',
    name: 'Puerto Rico',
    adjective: 'Puerto Rico',
    code3: 'PRI',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  QA: {
    code: 'QA',
    dialCode: '+974',
    emoji: '🇶🇦',
    name: 'Qatar',
    adjective: 'Qatari',
    code3: 'QAT',
    currency: 'QAR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  RE: {
    code: 'RE',
    dialCode: '+262',
    emoji: '🇫🇷',
    name: 'Réunion',
    adjective: 'Réunionese',
    code3: 'REU',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: false,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  RO: {
    code: 'RO',
    dialCode: '+40',
    emoji: '🇷🇴',
    name: 'Romania',
    adjective: 'Romanian',
    code3: 'ROU',
    currency: 'RON',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'National Identification Number (Cod Numeric Personal)',
    taxIdentifierName: 'Cod Numeric Personal sau Codul de identificare fiscală (CNP / CIF)',
    taxIdentifierShortName: 'CNP / CIF',
    taxIdentifierPlaceholder: '0000000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  RS: {
    code: 'RS',
    dialCode: '+381',
    emoji: '🇷🇸',
    name: 'Serbia',
    adjective: 'Serbian',
    code3: 'SRB',
    currency: 'RSD',
    isEEA: false,
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  RU: {
    code: 'RU',
    dialCode: '+7',
    emoji: '🇷🇺',
    name: 'Russia',
    adjective: 'Russian',
    code3: 'RUS',
    currency: 'RUB',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  RW: {
    code: 'RW',
    dialCode: '+250',
    emoji: '🇷🇼',
    name: 'Rwanda',
    adjective: 'Rwandan',
    code3: 'RWA',
    currency: 'RWF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SH: {
    code: 'SH',
    dialCode: '+290',
    emoji: '🇸🇭',
    name: 'St. Helena',
    adjective: 'Saint Helena',
    code3: 'SHN',
    currency: 'SHP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  KN: {
    code: 'KN',
    dialCode: '+1869',
    emoji: '🇰🇳',
    name: 'St. Kitts & Nevis',
    adjective: 'Kittian and Nevisian',
    code3: 'KNA',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  LC: {
    code: 'LC',
    dialCode: '+1758',
    emoji: '🇱🇨',
    name: 'St. Lucia',
    adjective: 'Saint Lucian',
    code3: 'LCA',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VC: {
    code: 'VC',
    dialCode: '+1784',
    emoji: '🇻🇨',
    name: 'St. Vincent & Grenadines',
    adjective: 'Saint Vincent and the Grenadines',
    code3: 'VCT',
    currency: 'XCD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  WS: {
    code: 'WS',
    dialCode: '+685',
    emoji: '🇼🇸',
    name: 'Samoa',
    adjective: 'Samoan',
    code3: 'WSM',
    currency: 'WST',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SM: {
    code: 'SM',
    dialCode: '+378',
    emoji: '🇸🇲',
    name: 'San Marino',
    adjective: 'San Marinese',
    code3: 'SMR',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ST: {
    code: 'ST',
    dialCode: '+239',
    emoji: '🇸🇹',
    name: 'São Tomé & Príncipe',
    adjective: 'Sao Tomean',
    code3: 'STP',
    currency: 'STD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SA: {
    code: 'SA',
    dialCode: '+966',
    emoji: '🇸🇦',
    name: 'Saudi Arabia',
    adjective: 'Saudi',
    code3: 'SAU',
    currency: 'SAR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SN: {
    code: 'SN',
    dialCode: '+221',
    emoji: '🇸🇳',
    name: 'Senegal',
    adjective: 'Senegalese',
    code3: 'SEN',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SC: {
    code: 'SC',
    dialCode: '+248',
    emoji: '🇸🇨',
    name: 'Seychelles',
    adjective: 'Seychellois',
    code3: 'SYC',
    currency: 'SCR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SL: {
    code: 'SL',
    dialCode: '+232',
    emoji: '🇸🇱',
    name: 'Sierra Leone',
    adjective: 'Sierra Leonean',
    code3: 'SLE',
    currency: 'SLL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SG: {
    code: 'SG',
    dialCode: '+65',
    emoji: '🇸🇬',
    name: 'Singapore',
    adjective: 'Singaporean',
    code3: 'SGP',
    currency: 'SGD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SK: {
    code: 'SK',
    dialCode: '+421',
    emoji: '🇸🇰',
    name: 'Slovakia',
    adjective: 'Slovakian',
    code3: 'SVK',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal number (Rodné číslo)',
    taxIdentifierName: 'Personal number (Rodné číslo)',
    taxIdentifierPlaceholder: '000000000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'HIDDEN',
  },
  SI: {
    code: 'SI',
    dialCode: '+386',
    emoji: '🇸🇮',
    name: 'Slovenia',
    adjective: 'Slovenian',
    code3: 'SVN',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal Identification Number (EMŠO: Enotna Matična Številka Občana)',
    taxIdentifierName: 'Tax Identifier Number (Davčna številka)',
    taxIdentifierPlaceholder: '00000000',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  SB: {
    code: 'SB',
    dialCode: '+677',
    emoji: '🇸🇧',
    name: 'Solomon Islands',
    adjective: 'Solomon Islander',
    code3: 'SLB',
    currency: 'SBD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SO: {
    code: 'SO',
    dialCode: '+252',
    emoji: '🇸🇴',
    name: 'Somalia',
    adjective: 'Somali',
    code3: 'SOM',
    currency: 'SOS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ZA: {
    code: 'ZA',
    dialCode: '+27',
    emoji: '🇿🇦',
    name: 'South Africa',
    adjective: 'South African',
    code3: 'ZAF',
    currency: 'ZAR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ES: {
    code: 'ES',
    dialCode: '+34',
    emoji: '🇪🇸',
    name: 'Spain',
    adjective: 'Spanish',
    code3: 'ESP',
    currency: 'EUR',
    symbol: '€',
    isEEA: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Tax identification number (Código de identificación fiscal)',
    taxIdentifierName:
      'Documento Nacional de Identidad / Numero de Identidad de Extranjero (DNI / NIE)',
    taxIdentifierShortName: 'DNI / NIE',
    taxIdentifierPlaceholder: 'A0000000A',
    isUsingSeparateTIN: true,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'EU',
    status: 'WAITLISTED',
  },
  LK: {
    code: 'LK',
    dialCode: '+94',
    emoji: '🇱🇰',
    name: 'Sri Lanka',
    adjective: 'Sri Lankan',
    code3: 'LKA',
    currency: 'LKR',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SD: {
    code: 'SD',
    dialCode: '+249',
    emoji: '🇸🇩',
    name: 'Sudan',
    adjective: 'Sudanese',
    code3: 'SDN',
    currency: 'SDG',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SR: {
    code: 'SR',
    dialCode: '+597',
    emoji: '🇸🇷',
    name: 'Suriname',
    adjective: 'Surinamer',
    code3: 'SUR',
    currency: 'SRD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SJ: {
    code: 'SJ',
    dialCode: '+47',
    emoji: '🇸🇯',
    name: 'Svalbard & Jan Mayen',
    adjective: 'Svalbard and Jan Mayen',
    code3: 'SJM',
    currency: 'NOK',
    symbol: 'Kr',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SZ: {
    code: 'SZ',
    dialCode: '+268',
    emoji: '🇸🇿',
    name: 'Eswatini',
    adjective: 'Swazi',
    code3: 'SWZ',
    currency: 'SZL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SE: {
    code: 'SE',
    dialCode: '+46',
    emoji: '🇸🇪',
    name: 'Sweden',
    adjective: 'Swedish',
    code3: 'SWE',
    currency: 'SEK',
    isEEA: true,
    showInEuOnboarding: true,
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'Personal identity number',
    taxIdentifierName: 'Personal Identification Number (Personnummer)',
    taxIdentifierPlaceholder: '000000-0000',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'SUPPORTED',
    legalEntities: ['sda'],
    instrumentsUniverses: ['CRYPTO'],
    taxNumberFlow: 'required',
  },
  CH: {
    code: 'CH',
    dialCode: '+41',
    emoji: '🇨🇭',
    name: 'Switzerland',
    adjective: 'Swiss',
    code3: 'CHE',
    currency: 'CHF',
    symbol: 'Fr',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'WAITLISTED',
  },
  SS: {
    code: 'SS',
    dialCode: '+211',
    emoji: '🇸🇸',
    name: 'South Sudan',
    adjective: 'South Sudanese',
    code3: 'SSD',
    currency: 'SSP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  SY: {
    code: 'SY',
    dialCode: '+963',
    emoji: '🇸🇾',
    name: 'Syria',
    adjective: 'Syrian',
    code3: 'SYR',
    currency: 'SYP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TW: {
    code: 'TW',
    dialCode: '+886',
    emoji: '🇹🇼',
    name: 'Taiwan',
    adjective: 'Taiwanese',
    code3: 'TWN',
    currency: 'TWD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TJ: {
    code: 'TJ',
    dialCode: '+992',
    emoji: '🇹🇯',
    name: 'Tajikistan',
    adjective: 'Tajik',
    code3: 'TJK',
    currency: 'TJS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TZ: {
    code: 'TZ',
    dialCode: '+255',
    emoji: '🇹🇿',
    name: 'Tanzania',
    adjective: 'Tanzanian',
    code3: 'TZA',
    currency: 'TZS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TH: {
    code: 'TH',
    dialCode: '+66',
    emoji: '🇹🇭',
    name: 'Thailand',
    adjective: 'Thai',
    code3: 'THA',
    currency: 'THB',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TL: {
    code: 'TL',
    dialCode: '+670',
    emoji: '🇹🇱',
    name: 'Timor-Leste',
    adjective: 'East Timorese',
    code3: 'TLS',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TG: {
    code: 'TG',
    dialCode: '+228',
    emoji: '🇹🇬',
    name: 'Togo',
    adjective: 'Togolese',
    code3: 'TGO',
    currency: 'XOF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TK: {
    code: 'TK',
    dialCode: '+690',
    emoji: '🇹🇰',
    name: 'Tokelau',
    adjective: 'Tokelau',
    code3: 'TKL',
    currency: 'NZD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TO: {
    code: 'TO',
    dialCode: '+676',
    emoji: '🇹🇴',
    name: 'Tonga',
    adjective: 'Tongan',
    code3: 'TON',
    currency: 'TOP',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TT: {
    code: 'TT',
    dialCode: '+1868',
    emoji: '🇹🇹',
    name: 'Trinidad & Tobago',
    adjective: 'Trinidadian or Tobagonian',
    code3: 'TTO',
    currency: 'TTD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TN: {
    code: 'TN',
    dialCode: '+216',
    emoji: '🇹🇳',
    name: 'Tunisia',
    adjective: 'Tunisian',
    code3: 'TUN',
    currency: 'TND',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TR: {
    code: 'TR',
    dialCode: '+90',
    emoji: '🇹🇷',
    name: 'Turkey',
    adjective: 'Turkish',
    code3: 'TUR',
    currency: 'TRY',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TM: {
    code: 'TM',
    dialCode: '+993',
    emoji: '🇹🇲',
    name: 'Turkmenistan',
    adjective: 'Turkmenistan',
    code3: 'TKM',
    currency: 'TMT',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TC: {
    code: 'TC',
    dialCode: '+1649',
    emoji: '🇹🇨',
    name: 'Turks & Caicos Islands',
    adjective: 'Turks and Caicos Islands',
    code3: 'TCA',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  TV: {
    code: 'TV',
    dialCode: '+688',
    emoji: '🇹🇻',
    name: 'Tuvalu',
    adjective: 'Tuvaluan',
    code3: 'TUV',
    currency: 'AUD',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  UG: {
    code: 'UG',
    dialCode: '+256',
    emoji: '🇺🇬',
    name: 'Uganda',
    adjective: 'Ugandan',
    code3: 'UGA',
    currency: 'UGX',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  UA: {
    code: 'UA',
    dialCode: '+380',
    emoji: '🇺🇦',
    name: 'Ukraine',
    adjective: 'Ukrainian',
    code3: 'UKR',
    currency: 'UAH',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AE: {
    code: 'AE',
    dialCode: '+971',
    emoji: '🇦🇪',
    name: 'United Arab Emirates',
    adjective: 'Emirian',
    code3: 'ARE',
    currency: 'AED',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  GB: {
    code: 'GB',
    dialCode: '+44',
    emoji: '🇬🇧',
    name: 'United Kingdom',
    adjective: 'British',
    code3: 'GBR',
    currency: 'GBP',
    symbol: '£',
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    nationalIdentifierName: 'UK National Insurance number',
    taxIdentifierPlaceholder: 'AA000000A',
    phoneNumberPlaceholder: '7911 123456',
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    region: 'UK',
    status: 'HIDDEN',
  },
  US: {
    code: 'US',
    dialCode: '+1',
    emoji: '🇺🇸',
    name: 'United States',
    adjective: 'American',
    code3: 'USA',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  UY: {
    code: 'UY',
    dialCode: '+598',
    emoji: '🇺🇾',
    name: 'Uruguay',
    adjective: 'Uruguayan',
    code3: 'URY',
    currency: 'UYU',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  UZ: {
    code: 'UZ',
    dialCode: '+998',
    emoji: '🇺🇿',
    name: 'Uzbekistan',
    adjective: 'Uzbekistani',
    code3: 'UZB',
    currency: 'UZS',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VU: {
    code: 'VU',
    dialCode: '+678',
    emoji: '🇻🇺',
    name: 'Vanuatu',
    adjective: 'Vanuatu',
    code3: 'VUT',
    currency: 'VUV',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VE: {
    code: 'VE',
    dialCode: '+58',
    emoji: '🇻🇪',
    name: 'Venezuela',
    adjective: 'Venezuelan',
    code3: 'VEN',
    currency: 'VEF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VN: {
    code: 'VN',
    dialCode: '+84',
    emoji: '🇻🇳',
    name: 'Vietnam',
    adjective: 'Vietnamese',
    code3: 'VNM',
    currency: 'VND',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VG: {
    code: 'VG',
    dialCode: '+1284',
    emoji: '🇻🇬',
    name: 'British Virgin Islands',
    adjective: 'Virgin Islands, British',
    code3: 'VGB',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  VI: {
    code: 'VI',
    dialCode: '+1340',
    emoji: '🇻🇮',
    name: 'U.S. Virgin Islands',
    adjective: 'Virgin Islands, U.S.',
    code3: 'VIR',
    currency: 'USD',
    symbol: '$',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  WF: {
    code: 'WF',
    dialCode: '+681',
    emoji: '🇼🇫',
    name: 'Wallis & Futuna',
    adjective: 'Welsh',
    code3: 'WLF',
    currency: 'XPF',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  YE: {
    code: 'YE',
    dialCode: '+967',
    emoji: '🇾🇪',
    name: 'Yemen',
    adjective: 'Yemenite',
    code3: 'YEM',
    currency: 'YER',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  YT: {
    code: 'YT',
    dialCode: '+262',
    emoji: '🇫🇷',
    name: 'Mayotte',
    adjective: 'Mahorais',
    code3: 'MYT',
    currency: 'EUR',
    symbol: '€',
    showInEuOnboarding: false,
    hideInNationality: true,
    isPassportPriorityIdentifier: true,
    isCONCAT: true,
    isUsingSeparateTIN: false,
    showInCountrySelector: true,
    region: 'EU',
    status: 'HIDDEN',
    phoneNumberPlaceholder: '123 45 67 89',
  },
  ZM: {
    code: 'ZM',
    dialCode: '+260',
    emoji: '🇿🇲',
    name: 'Zambia',
    adjective: 'Zambian',
    code3: 'ZMB',
    currency: 'ZMK',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  ZW: {
    code: 'ZW',
    dialCode: '+263',
    emoji: '🇿🇼',
    name: 'Zimbabwe',
    adjective: 'Zimbabwean',
    code3: 'ZWE',
    currency: 'ZWL',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  XK: {
    code: 'XK',
    dialCode: '+383',
    emoji: '🇽🇰',
    name: 'Kosovo',
    adjective: 'Kosovar',
    code3: 'XKX',
    currency: 'EUR',
    symbol: '€',
    isPassportPriorityIdentifier: true,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
} as const;

export const currencies = Object.values(countryDataByCode).map((val) => val.currency);

type SupportedCountryCodesBo = SupportedCountryCodes | 'AI' | 'AS';

export const countryDataByCodeBo: Record<
  SupportedCountryCodesBo,
  Omit<CountryData, 'code'> & { code: SupportedCountryCodesBo }
> = {
  ...countryDataByCode,
  AI: {
    code: 'AI',
    dialCode: '+1264',
    emoji: '🇦🇮',
    name: 'Anguilla',
    adjective: 'Anguillan',
    code3: 'AIA',
    currency: 'XCD',
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
  AS: {
    code: 'AS',
    dialCode: '+1684',
    emoji: '🇦🇸',
    name: 'American Samoa',
    adjective: 'American Samoan',
    code3: 'ASM',
    currency: 'USD',
    isPassportPriorityIdentifier: false,
    isCONCAT: false,
    isUsingSeparateTIN: false,
    showInCountrySelector: false,
    showInEuOnboarding: false,
    status: 'HIDDEN',
  },
} as const;
